import { Row, Col } from 'antd'
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useCallback, useRef } from 'react'
import Http from "@/utils/http";
import ConstValue from "@/utils/value";
import './index.scss'
import NavLink from '@/components/NavLink'
import { Dropdown, Checkbox, Tag } from 'antd';

export default function AllProducts() {
  const [allData, setAllData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]); // 存储筛选选项数据
  const [selectedFilters, setSelectedFilters] = useState({
    0: [], // 第一个筛选器的选中项
    1: [], // 第二个筛选器的选中项  
    2: [], // 第三个筛选器的选中项
    3: []  // 第四个筛选器的选中项
  });
  
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const pageSize = 20;
  
  const navigate = useNavigate();
  const observerRef = useRef();
  const loadingRef = useRef(false);
  const containerRef = useRef();
  const selectedFiltersRef = useRef(selectedFilters);

  // 初始化引用
  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  const lastItemRef = useCallback(node => {
    if (loadingRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !initialLoading && !filterLoading && !loading) {
        loadMoreData();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [hasMore, initialLoading, filterLoading, loading]);

  useEffect(() => {
    getFilterData();
    getAllProducts();
  }, []);

  useEffect(() => {
    if (isDataLoaded) {
      setFilterLoading(true);
      const timer = setTimeout(() => {
        filterData();
        setFilterLoading(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [selectedFilters, isDataLoaded]);

  // 获取筛选器选项数据
  const getFilterData = async () => {
    try {
      const res = await Http.to.items("filter").readByQuery({
        fields: ['*'],
        filter: {}
      });
      setFilterOptions(res.data || []);
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  }

  // 获取所有产品数据
  const getAllProducts = async () => {
    try {
      setInitialLoading(true);
      const res = await Http.to.items("Product_new").readByQuery({
        sort: ['id'],
        filter: {},
        limit: -1
      });
      
      setAllData(res.data || []);
      setIsDataLoaded(true);
      applyCurrentFilters(res.data || []);
      
    } catch (error) {
      console.error('获取产品数据失败:', error);
    } finally {
      setTimeout(() => {
        setInitialLoading(false);
      }, 800);
    }
  }

  // 应用当前筛选条件到数据
  const applyCurrentFilters = (data) => {
    const currentFilters = selectedFiltersRef.current;
    
    if (Object.values(currentFilters).every(arr => arr.length === 0)) {
      const initialData = data.slice(0, pageSize);
      const mergedInitialData = mergeProductsByTitle(initialData);
      setDisplayData(mergedInitialData);
      setHasMore(data.length > pageSize);
    } else {
      filterDataWithSource(data, currentFilters);
    }
  }

  // 使用指定数据源和筛选条件进行筛选
  const filterDataWithSource = (dataSource, currentFilters = selectedFiltersRef.current) => {
    const filtered = dataSource.filter(item => {
      if (!item?.Part_No) return false;
      
      // 筛选器0: 名称匹配（多选OR逻辑）
      const match0 = currentFilters[0].length === 0 || 
        currentFilters[0].some(filter => 
          item.Name && item.Name.toLowerCase().includes(filter.toLowerCase())
        );
      
      // 筛选器1: 波长匹配（多选OR逻辑）
      const match1 = currentFilters[1].length === 0 || 
        currentFilters[1].some(filter => 
          item.Wavelength && item.Wavelength.toLowerCase().includes(filter.toLowerCase())
        );
      
      // 筛选器2: 距离匹配（多选OR逻辑）
      let match2 = true;
      if (currentFilters[2].length > 0) {
        match2 = currentFilters[2].some(filter => {
          if (item.Reach_search) {
            const cleanReach = item.Reach_search.replace(/\s+/g, '').toLowerCase();
            const cleanFilter = filter.replace(/\s+/g, '').toLowerCase();
            return cleanReach === cleanFilter || 
                   cleanReach.includes(cleanFilter) || 
                   cleanFilter.includes(cleanReach);
          }
          return false;
        });
      }
      
      // 筛选器3: 其他名称匹配（多选OR逻辑）
      const match3 = currentFilters[3].length === 0 || 
        currentFilters[3].some(filter => 
          item.Name && item.Name.toLowerCase().includes(filter.toLowerCase())
        );
      
      // 所有筛选器之间是AND关系
      return match0 && match1 && match2 && match3;
    });
    
    const mergedProducts = mergeProductsByTitle(filtered);
    const firstPageData = mergedProducts.slice(0, pageSize);
    setDisplayData(firstPageData);
    setHasMore(mergedProducts.length > pageSize);
    setPage(1);
  }

  // 合并相同标题的产品
  const mergeProductsByTitle = (products) => {
    const mergedMap = {};
    
    products.forEach(product => {
      const title = product.Name;
      if (!mergedMap[title]) {
        mergedMap[title] = {
          ...product,
          mergedProducts: [product],
          mergedPartNos: [product.Part_No],
          mergedCount: 1
        };
      } else {
        mergedMap[title].mergedProducts.push(product);
        mergedMap[title].mergedPartNos.push(product.Part_No);
        mergedMap[title].mergedCount += 1;
      }
    });
    
    return Object.values(mergedMap);
  }

  // 筛选数据（使用已加载的allData）
  const filterData = () => {
    if (allData.length === 0) return;
    filterDataWithSource(allData);
  }

  // 加载更多数据
  const loadMoreData = () => {
    if (loadingRef.current) return;
    
    loadingRef.current = true;
    setLoading(true);
    
    setTimeout(() => {
      const currentFilters = selectedFiltersRef.current;
      
      const filtered = allData.filter(item => {
        if (!item?.Part_No) return false;
        
        const match0 = currentFilters[0].length === 0 || 
          currentFilters[0].some(filter => 
            item.Name && item.Name.toLowerCase().includes(filter.toLowerCase())
          );
        
        const match1 = currentFilters[1].length === 0 || 
          currentFilters[1].some(filter => 
            item.Wavelength && item.Wavelength.toLowerCase().includes(filter.toLowerCase())
          );
        
        let match2 = true;
        if (currentFilters[2].length > 0) {
          match2 = currentFilters[2].some(filter => {
            if (item.Reach_search) {
              const cleanReach = item.Reach_search.replace(/\s+/g, '').toLowerCase();
              const cleanFilter = filter.replace(/\s+/g, '').toLowerCase();
              return cleanReach === cleanFilter || 
                     cleanReach.includes(cleanFilter) || 
                     cleanFilter.includes(cleanReach);
            }
            return false;
          });
        }
        
        const match3 = currentFilters[3].length === 0 || 
          currentFilters[3].some(filter => 
            item.Name && item.Name.toLowerCase().includes(filter.toLowerCase())
          );
        
        return match0 && match1 && match2 && match3;
      });
      
      const mergedProducts = mergeProductsByTitle(filtered);
      const nextPage = page + 1;
      const endIndex = nextPage * pageSize;
      const newData = mergedProducts.slice(0, endIndex);
      
      setDisplayData(newData);
      setPage(nextPage);
      setHasMore(mergedProducts.length > endIndex);
      
      setLoading(false);
      loadingRef.current = false;
    }, 16);
  }

  // 处理筛选项选择变化
  const handleFilterChange = (filterIndex, value) => {
    setSelectedFilters(prev => {
      const newFilters = { ...prev };
      const currentSelections = newFilters[filterIndex];
      
      if (currentSelections.includes(value)) {
        // 如果已经选中，则移除
        newFilters[filterIndex] = currentSelections.filter(item => item !== value);
      } else {
        // 如果未选中，则添加
        newFilters[filterIndex] = [...currentSelections, value];
      }
      
      return newFilters;
    });
  };

  // 清除单个筛选器的所有选择
  const clearFilter = (filterIndex) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterIndex]: []
    }));
  };

  // 清除所有筛选器
  const clearAllFilters = () => {
    setSelectedFilters({
      0: [], 1: [], 2: [], 3: []
    });
  };

  // 创建下拉菜单项
  const createMenuItems = (filterIndex) => {
    const filterData = filterOptions[filterIndex];
    if (!filterData || !filterData.option) return [];
    
    const menuItems = filterData.option.map((item, index) => ({
      key: `${filterIndex}-${index}`,
      label: (
        <div 
          style={{ 
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleFilterChange(filterIndex, item.project);
          }}
        >
          <Checkbox 
            checked={selectedFilters[filterIndex].includes(item.project)}
            style={{ marginRight: 8 }}
          />
          <span>{item.project}</span>
        </div>
      ),
    }));

    // 添加清除选项
    menuItems.push({
      type: 'divider',
      key: `divider-${filterIndex}`
    });
    menuItems.push({
      key: `clear-${filterIndex}`,
      label: (
        <div 
          style={{ 
            padding: '8px 12px',
            color: '#ff4d4f',
            cursor: 'pointer',
            textAlign: 'center'
          }}
          onClick={() => clearFilter(filterIndex)}
        >
          Clear Selection
        </div>
      ),
    });

    return menuItems;
  };

  const toProducts3 = (id) => {
    navigate('/productsInfo/' + id);
  };

  const toPage = (address) => {
    if (address.indexOf('http') !== -1) {
      window.open(address);
      return;
    }
    navigate('/' + address);
  };

  const geturl = (url) => {
    if (!url) return '';
    return url.split(';')[0];
  };

  // 渲染产品描述（合并后的Part_No）
  const renderDescription = (item) => {
    if (item.mergedPartNos && item.mergedPartNos.length > 1) {
      const maxDisplay = 3;
      const displayItems = item.mergedPartNos.slice(0, maxDisplay);
      const remainingCount = item.mergedPartNos.length - maxDisplay;
      
      return (
        <>
          {displayItems.map((partNo, index) => (
            <span key={index} className="part-no-item">
              {partNo}
              {index < displayItems.length - 1 && ' '}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className="more-indicator"> ... (+{remainingCount} more)</span>
          )}
        </>
      );
    }
    return <span dangerouslySetInnerHTML={{ __html: item.Part_No }}></span>;
  };

  // 获取筛选器显示文本
  const getFilterDisplayText = (filterIndex) => {
    const selected = selectedFilters[filterIndex];
    if (selected.length === 0) {
      return filterOptions[filterIndex]?.title || 'Select';
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return `${selected[0]} +${selected.length - 1}`;
  };

  return (
    <div className='all_products' ref={containerRef}>
      <NavLink title1={'Products · Solution'} link1={() => { toPage('Solution') }} title2={'All Products'} />
      <div className='top_info'>
        <div>
          <span>All Products</span>
        </div>
      </div>
      
     
      <div className='filter'>
        <Row justify={'center'}>
          <Col xs={24} lg={11}>
            <Row justify={'space-between'}>
              <Col xs={24} md={11}>
                <Dropdown 
                  menu={{ items: createMenuItems(0) }} 
                  placement="bottom" 
                  arrow
                  overlayClassName="multi-select-dropdown"
                >
                  <div className='filter_box'>
                    <div className='icon1'></div>
                    <div>
                      <span className="filter-title">{filterOptions[0]?.title}</span>
                      {selectedFilters[0].length > 0 && (
                        <span className="filter-selected">{getFilterDisplayText(0)}</span>
                      )}
                    </div>
                  </div>
                </Dropdown>
              </Col>
              <Col xs={24} md={11}>
                <Dropdown 
                  menu={{ items: createMenuItems(1) }} 
                  placement="bottom" 
                  arrow
                  overlayClassName="multi-select-dropdown"
                >
                  <div className='filter_box'>
                    <div className='icon2'></div>
                    <div>
                      <span className="filter-title">{filterOptions[1]?.title}</span>
                      {selectedFilters[1].length > 0 && (
                        <span className="filter-selected">{getFilterDisplayText(1)}</span>
                      )}
                    </div>
                  </div>
                </Dropdown>
              </Col>
            </Row>
          </Col>
          <Col xs={24} lg={11}>
            <Row justify={'space-between'}>
              <Col xs={24} md={11}>
                <Dropdown 
                  menu={{ items: createMenuItems(2) }} 
                  placement="bottom" 
                  arrow
                  overlayClassName="multi-select-dropdown"
                >
                  <div className='filter_box'>
                    <div className='icon3'></div>
                    <div>
                      <span className="filter-title">{filterOptions[2]?.title}</span>
                      {selectedFilters[2].length > 0 && (
                        <span className="filter-selected">{getFilterDisplayText(2)}</span>
                      )}
                    </div>
                  </div>
                </Dropdown>
              </Col>
              <Col xs={24} md={11}>
                <Dropdown 
                  menu={{ items: createMenuItems(3) }} 
                  placement="bottom" 
                  arrow
                  overlayClassName="multi-select-dropdown"
                >
                  <div className='filter_box'>
                    <div className='icon4'></div>
                    <div>
                      <span className="filter-title">{filterOptions[3]?.title}</span>
                      {selectedFilters[3].length > 0 && (
                        <span className="filter-selected">{getFilterDisplayText(3)}</span>
                      )}
                    </div>
                  </div>
                </Dropdown>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

 {/* 选中的筛选标签 */}
      {(Object.values(selectedFilters).some(arr => arr.length > 0)) && (
        <div className="selected-filters-tags">
          <Row justify={'center'}>
            <Col xs={24} lg={22}>
              <div style={{ padding: '16px 0', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[0, 1, 2, 3].map(filterIndex => 
                  selectedFilters[filterIndex].map((value, index) => (
                    <Tag
                      key={`${filterIndex}-${index}`}
                      closable
                      onClose={() => handleFilterChange(filterIndex, value)}
                      color={filterIndex === 0 ? 'blue' : filterIndex === 1 ? 'green' : filterIndex === 2 ? 'orange' : 'purple'}
                    >
                      {filterOptions[filterIndex]?.title}: {value}
                    </Tag>
                  ))
                )}
                <Tag 
                  closable 
                  onClose={clearAllFilters}
                  color="red"
                >
                  Clear All
                </Tag>
              </div>
            </Col>
          </Row>
        </div>
      )}

      <div className='info'>
        <Row justify={'center'}>
          <Col xs={24} className="productright">
            <div className='items'>
              {initialLoading && (
                <>
                  {[...Array(9)].map((_, index) => (
                    <div key={index} className='item skeleton'>
                      <div className='img-container'>
                        <div className='img skeleton-img'></div>
                      </div>
                      <div className='info_item'>
                        <div className='title skeleton-text'></div>
                        <div className='description skeleton-text short'></div>
                        <div className='readmore skeleton-text shorter'></div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {filterLoading && (
                <>
                  {[...Array(6)].map((_, index) => (
                    <div key={`filter-${index}`} className='item skeleton'>
                      <div className='img-container'>
                        <div className='img skeleton-img'></div>
                      </div>
                      <div className='info_item'>
                        <div className='title skeleton-text'></div>
                        <div className='description skeleton-text short'></div>
                        <div className='readmore skeleton-text shorter'></div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {!initialLoading && !filterLoading && displayData.map((item, index) => {
                const isLastItem = index === displayData.length - 1;
                const ref = isLastItem && hasMore ? lastItemRef : null;
                const isMerged = item.mergedProducts && item.mergedProducts.length > 1;
                
                return (
                  <div 
                    key={`${item.id}-${index}`} 
                    className='item' 
                    onClick={() => toProducts3(item?.id)}
                    ref={ref}
                  >
                    <div className='img-container'>
                      <div 
                        className='img' 
                        style={{ 
                          backgroundImage: `url(${ConstValue.url + "assets/" + geturl(item.img)})`
                        }}
                      >
                        <div className='mask'></div>
                        {isMerged && (
                          <div className='merged-badge'>
                            {item.mergedCount} 
                          </div>
                        )}
                      </div>
                    </div>
                    <div className='info_item'>
                      <span className='title'>{item?.Name}</span>
                      <span className='description'>
                        {renderDescription(item)}
                      </span>
                      <span className='readmore'>READ MORE<span></span></span>
                    </div>
                  </div>
                )
              })}
              
              {loading && (
                <>
                  {[...Array(3)].map((_, index) => (
                    <div key={`loading-${index}`} className='item skeleton loading-skeleton'>
                      <div className='img-container'>
                        <div className='img skeleton-img'></div>
                      </div>
                      <div className='info_item'>
                        <div className='title skeleton-text'></div>
                        <div className='description skeleton-text short'></div>
                        <div className='readmore skeleton-text shorter'></div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {!hasMore && displayData.length > 0 && !loading && (
                <div className="no-more-indicator">
                  {/* 可以添加"没有更多数据"的提示 */}
                </div>
              )}
              
              {!initialLoading && !filterLoading && displayData.length === 0 && !loading && (
                <div className="empty-state">
                  No matching product found ...
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}